import { CommonActions } from '@bugbug/core/actions/actions';
import { createAction, createSlice, isAnyOf } from '@reduxjs/toolkit';

import type { TestChangeNotification } from './tests.types';
import type { PayloadAction } from '@reduxjs/toolkit';

import type { URLString } from '@bugbug/core/types/aliases';
import type { Step } from '@bugbug/core/types/steps';
import type { Test, TestRun } from '@bugbug/core/types/tests';

import { BackendActions } from '../websocket/backend.actions';

import { testsApi } from './tests.api';

interface TestsState {
  currentTestId?: Test['id'];
  currentTestRunId?: TestRun['id'];
  changeNotificationByTestId: Record<Test['id'], TestChangeNotification>;
  elementsScreenshots: Record<Step['id'], URLString>;
  isSingleLoading: boolean;
  defaultStepsParams: unknown;
}

const initialState: TestsState = {
  currentTestId: undefined,
  currentTestRunId: undefined,
  changeNotificationByTestId: {},
  elementsScreenshots: {},
  isSingleLoading: false,
  defaultStepsParams: {},
};

const TestsSlice = createSlice({
  name: 'tests',
  initialState,

  reducers: {
    resetCurrentTest(state) {
      state.currentTestId = undefined;
      state.currentTestRunId = undefined;
    },
    clearChangedNotification(state, action: PayloadAction<Test['id']>) {
      delete state.changeNotificationByTestId[action.payload];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(BackendActions.setRecordingState, (state) => {
        state.isSingleLoading = false;
      })
      .addCase(BackendActions.testChanged, (state, action) => {
        const { testId, triggeredBy } = action.payload;
        if (state.currentTestId === testId) {
          state.changeNotificationByTestId[testId] = triggeredBy;
        }
      })
      .addMatcher(
        isAnyOf(CommonActions.setIsSingleLoading, BackendActions.setCurrentTestLoading),
        (state, action) => {
          const { testId, isLoading } = action.payload;
          if (state.currentTestId === testId) {
            state.isSingleLoading = isLoading;
          }
        },
      )
      .addMatcher(testsApi.endpoints.getCurrentTest.matchPending, (state, action) => {
        state.currentTestId = action.meta.arg.originalArgs.id;
        state.currentTestRunId = action.meta.arg.originalArgs.testRunId;
      });
  },
});

export const TestsActions = {
  ...TestsSlice.actions,

  setPlaybackCursorPositionAfterStep: createAction<{ step: Step }>(
    'tests/setPlaybackCursorPositionAfterStep',
  ),

  refreshPlaybackCursorPosition: createAction('tests/refreshPlaybackCursorPosition'),
};

export default TestsSlice.reducer;
